import { useEffect, useState, useContext, useRef } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import DataContext from '../../services/DataContext';
import { License, LicenseArchive } from '../../types/licenseTypes';
import GenericLicensesTable from '../uiTools/LicensesCommon/GenericLicensesTable';
import { SnackBarProps } from '../../types/snackBarTypes';
import SnackBar from '../SnackBar/SnackBar';
import { useDebounce } from '../Hooks/useDebouce';
import {
  GridRowSelectionModel,
  GridSortModel,
  GridFilterModel,
} from '@mui/x-data-grid';
import { buildMongoWhere } from '../../services/buildMongoWhere';

dayjs.locale('fr');

interface LicensesTableProps {
  handleDeleteLicense: (license: License | LicenseArchive) => void;
  handleOpenAddDialog: () => void;
  handleOpenEditDialog: (license: License | LicenseArchive) => void;
  handleOpenBulkDeleteDialog?: (
    selectedLicenses: (License | LicenseArchive)[],
  ) => void;
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  searchValue: string;
  licenseChangeIndicator: boolean;
  selectionModel: GridRowSelectionModel;
  handleSelectionModelChange: (
    newSelectionModel: GridRowSelectionModel,
    licenses: License[],
  ) => void;
  selectedLicensesForDeletion: (License | LicenseArchive)[];
}

const LicensesTable: React.FC<LicensesTableProps> = ({
  handleDeleteLicense,
  handleOpenAddDialog,
  handleOpenEditDialog,
  handleOpenBulkDeleteDialog,
  handleSearchChange,
  searchValue,
  licenseChangeIndicator,
  selectionModel,
  handleSelectionModelChange,
  selectedLicensesForDeletion,
}) => {
  const { getAll, getCountForPagination } = useContext(DataContext);
  const [initialized, setInitialized] = useState(false);

  const fetchLicensesDataTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const lastSearchValue = useRef('');
  const debouncedSearchValue = useDebounce(searchValue, 150);

  const [snackbarInfo, setSnackbarInfo] = useState<SnackBarProps>({
    key: 0,
    message: null,
    severity: null,
  });

  const [transformedLicenses, setTransformedLicenses] = useState<License[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'createdAt', sort: 'desc' },
  ]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  useEffect(() => {
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (!initialized) return;

    const fetchLicensesData = async () => {
      try {
        const activeSort = sortModel[0];
        const orderField = activeSort?.field ?? 'createdAt';
        const orderDirection = activeSort?.sort === 'asc' ? 'ASC' : 'DESC';
        const where = buildMongoWhere(filterModel);

        const result = await getAll(
          'licenses',
          pagination.pageSize,
          pagination.page * pagination.pageSize,
          debouncedSearchValue,
          orderField,
          orderDirection,
          where,
        );

        const licenses = result.data;
        //const totalCount = result.totalCount;

        const transformed = licenses.map((license: License, index: number) => ({
          ...license,
          id: license._id?.toString() ?? index.toString(),
          readedAt: license.readedAt
            ? dayjs(new Date(license.readedAt)).format('DD/MM/YYYY à HH:mm')
            : undefined,
          startDate: dayjs(new Date(license.startDate)).format('DD/MM/YYYY'),
          endDate: dayjs(new Date(license.endDate)).format('DD/MM/YYYY'),
          createdAt: dayjs(new Date(license.createdAt)).format('DD/MM/YYYY'),
        }));

        const count = await getCountForPagination(
          'licenses',
          debouncedSearchValue,
          where,
        );

        setTransformedLicenses(transformed);
        setRowCount(count);
        lastSearchValue.current = debouncedSearchValue;
      } catch (error) {
        setSnackbarInfo({
          key: new Date().getTime(),
          message: `Erreur dans l'extraction des données relatives aux licences : ${error}`,
          severity: 'error',
        });
      }
    };

    if (fetchLicensesDataTimeout.current) {
      clearTimeout(fetchLicensesDataTimeout.current);
    }

    if (searchValue !== lastSearchValue.current) {
      fetchLicensesDataTimeout.current = setTimeout(fetchLicensesData, 500);
    } else {
      fetchLicensesData();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialized,
    debouncedSearchValue,
    getAll,
    getCountForPagination,
    licenseChangeIndicator,
    pagination,
    sortModel,
    filterModel,
  ]);

  return (
    <>
      <GenericLicensesTable
        handleDeleteLicense={handleDeleteLicense}
        handleOpenAddDialog={handleOpenAddDialog}
        handleOpenEditDialog={handleOpenEditDialog}
        handleSearchChange={handleSearchChange}
        handleOpenBulkDeleteDialog={handleOpenBulkDeleteDialog}
        searchValue={searchValue}
        licenseChangeIndicator={licenseChangeIndicator}
        isArchive={false}
        licenses={transformedLicenses}
        rowCount={rowCount}
        paginationModel={pagination}
        sortModel={sortModel}
        onSortModelChange={newSortModel => {
          setSortModel(newSortModel);
          setPagination(prev => ({ ...prev, page: 0 }));
        }}
        filterModel={filterModel}
        onFilterModelChange={newFilterModel => {
          setFilterModel(newFilterModel);
          setPagination(prev => ({ ...prev, page: 0 }));
        }}
        selectionModel={selectionModel}
        handleSelectionModelChange={handleSelectionModelChange}
        selectedLicensesForDeletion={selectedLicensesForDeletion}
        onPaginationModelChange={newPagination => {
          if (
            initialized &&
            JSON.stringify(newPagination) !== JSON.stringify(pagination)
          ) {
            setPagination(newPagination);
          }
        }}
      />

      {snackbarInfo.message && snackbarInfo.severity && (
        <SnackBar
          key={snackbarInfo.key}
          message={snackbarInfo.message}
          severity={snackbarInfo.severity}
        />
      )}
    </>
  );
};

export default LicensesTable;
